import {
  EntityLinks,
  Goal,
  GoalProgressCount,
  GoalSortingMode,
  ID,
  LifeArea,
  lifeAreaOtherID,
  OrderDirection,
} from '@griply/types';

import {
  buildGoalsById,
  getGoalTopLevelLifeAreas,
} from '../get-goal-life-area';
import { sortItems } from '../sort-items';
import { getGoalListOrder } from './get-goal-list-order';
import { getPresentParentIds } from './get-present-parent-ids';

export type GetLifeAreaGoalOrderOptions = {
  // The goals the list renders — a goal whose parents are all absent sits at top level under its areas.
  goals: Goal[];
  // Sort context and inheritance source; defaults to `goals`.
  allGoals?: Goal[];
  lifeAreas: LifeArea[];
  links?: EntityLinks;
  sortMode?: GoalSortingMode | null;
  sortDirection: OrderDirection;
  goalGroupSorting?: Record<ID, ID[]>;
  taskCounts?: Map<ID, number>;
  goalProgressCounts?: Map<ID, GoalProgressCount>;
};

// One explicit order per life-area section, links-aware: a goal sits under every area it links to.
export const getLifeAreaGoalOrder = ({
  goals,
  allGoals = goals,
  lifeAreas,
  links,
  sortMode,
  sortDirection,
  goalGroupSorting,
  taskCounts,
  goalProgressCounts,
}: GetLifeAreaGoalOrderOptions): Map<ID, ID[]> => {
  const goalsById = buildGoalsById(allGoals);
  const renderedIds = new Set(goals.map(({ id }) => id));
  const cache = new Map<ID, ID[]>();
  const goalsByArea = new Map<ID, Goal[]>();
  const push = (areaId: ID, goal: Goal) =>
    goalsByArea.set(areaId, [...(goalsByArea.get(areaId) ?? []), goal]);

  goals.forEach((goal) => {
    const isNested = !!getPresentParentIds(goal, (id) => renderedIds.has(id), {
      links,
      goalsById,
    }).length;
    const areaIds = getGoalTopLevelLifeAreas(goal, {
      isNested,
      goalsById,
      links,
      cache,
    });
    areaIds.forEach((areaId) => push(areaId, goal));
    if (!areaIds.length && !isNested) {
      push(lifeAreaOtherID, goal);
    }
  });

  const orderOf = (areaGoals: Goal[], goalSorting?: ID[] | null): ID[] => {
    const effectiveSortMode = sortMode ?? GoalSortingMode.StartDate;
    if (effectiveSortMode === GoalSortingMode.Custom) {
      return sortItems(areaGoals, goalSorting ?? [], 'id').map(({ id }) => id);
    }
    return (
      getGoalListOrder({
        goals: areaGoals,
        allGoals,
        sortMode: effectiveSortMode,
        sortDirection,
        taskCounts,
        goalProgressCounts,
      }) ?? []
    );
  };

  return new Map<ID, ID[]>([
    ...lifeAreas.map(({ id, goalSorting }): [ID, ID[]] => [
      id,
      orderOf(goalsByArea.get(id) ?? [], goalSorting),
    ]),
    [
      lifeAreaOtherID,
      orderOf(goalsByArea.get(lifeAreaOtherID) ?? [], goalGroupSorting?.null),
    ],
  ]);
};
